import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CategoryService, GonogoInformationService } from "../../../../../../../../../api";
import {
	CategoryDialog,
	CategoryList,
	CustomButton,
	CustomIconButton,
	CustomTooltip,
	icon,
	IconComponent,
	SearchInput,
	ViewTabs,
} from "../../../../../../../../../common/components";
import {
	hasCompanyPermission,
	hasPermission,
	isSegFeatureEnabled,
	Permissions,
	SegFlags,
	translate,
} from "../../../../../../../../../common/providers";
import styles from "./CategoriesTabView.module.css";
import { debounce, updateTreeBranch } from "../../../../../../../../../common/utils";
import { updateNode } from "../../../../../../../slice/pdf/pdf-slice";
import { getCheckedCategoriesCount, getParentsIdsList } from "../../../../../../../../../common/utils/category-utils";
import { setDisableKeyEvents } from "../../../../../../../../../common/slice";
import { ACTION_TYPES } from "../../../../../../../../admin-categories/constants/constants";
import { getCategoryFromTree } from "../../../../../../../../admin-categories/utils/utils";
import { setExpandedIds } from "../../../../../../../slice/project-document/project-document-slice";
import { useApi, useTrackEvent } from "../../../../../../../../../common/hooks";
import { DOCUMENT_STATUSES } from "../../../../../../../constants/constants";

const extractCheckedIds = (categories) => {
	let ids = [];
	categories.forEach((c) => {
		if (c.matched) {
			ids.push(c.id);
		}
		if (Array.isArray(c.subThematic) && c.subThematic.length > 0) {
			ids = ids.concat(extractCheckedIds(c.subThematic));
		}
	});
	return ids;
};
const debouncedFunction = debounce((func) => func());

export default function CategoriesTabView({ disableEditing, informationId, pageStart, selectedMode }) {
	const { call: getByInformation } = useApi(CategoryService.getByInformation);
	const { call: patchByInformation } = useApi(CategoryService.patchByInformation);
	const { call: patchProjectTreeUpdate } = useApi(CategoryService.patchProjectTreeUpdate);
	const { call: getTreeChildrenForProject } = useApi(CategoryService.getTreeChildrenForProject);
	const { call: getAttachedCategoryIdsByInformation } = useApi(CategoryService.getAttachedCategoryIdsByInformation);
	const { call: attachInformationCategory } = useApi(GonogoInformationService.attachInformationCategory);
	const [checkedIds, setCheckedIds] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const [disabledIds, setDisabledIds] = useState([]);
	const [checkedCounts, setCheckedCounts] = useState({});
	const [showSelected, setShowSelected] = useState(false);
	const [openAddCategoryDialog, setOpenCategoryDialog] = useState(false);
	const [categorySearch, setCategorySearch] = useState("");
	const [categories, setCategories] = useState([]);
	const [categorySearchDebounced, setCategorySearchDebounced] = useState("");
	const [selectedTab, setSelectedTab] = useState(0);
	const [attachedCategoryIds, setAttachedCategoryIds] = useState([]);
	const projectId = useSelector(({ context }) => context.project.id);
	const expandedIds = useSelector(({ srProjectDocument }) => srProjectDocument.expandedIds);
	const disableKeyEvents = useSelector(({ context }) => context.disableKeyEvents);
	const isArchived = useSelector(({ srDocument }) => srDocument.status === DOCUMENT_STATUSES.ARCHIVED);
	const dispatch = useDispatch();
	const trackEvent = useTrackEvent();
	const isProjectDirector = useMemo(
		() =>
			hasCompanyPermission([Permissions.PROJECT_LEADER, Permissions.PROJECT_MANAGER]) &&
			hasPermission([Permissions.PROJECT_LEADER]),
		[]
	);
	const loadCategories = useCallback(
		(search) => {
			if (informationId) {
				setIsLoading(true);
				getByInformation({ informationId }, { search })
					.then((data) => {
						setCategories(data);
						setCheckedIds(extractCheckedIds(data));
						setIsLoading(false);
					})
					.catch((err) => {
						setIsLoading(false);
						console.error(err);
					});
			}
		},
		[getByInformation, informationId]
	);
	const isNotEmpty = useMemo(() => {
		if ((selectedTab === 1 || (disableEditing && selectedTab === 0)) && checkedIds?.length > 0) {
			return true;
		}
		if (selectedTab === 0 && !disableEditing && categories.length > 0) {
			return true;
		}
		return false;
	}, [checkedIds, disableEditing, selectedTab, categories]);
	useEffect(() => {
		if (!categorySearch) {
			setCheckedCounts(getCheckedCategoriesCount(categories, checkedIds));
		}
	}, [checkedIds, categories, categorySearch]);
	useEffect(
		() => () => {
			if (disableKeyEvents) {
				dispatch(setDisableKeyEvents(false));
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[]
	);
	useEffect(() => {
		loadCategories(categorySearchDebounced);
	}, [loadCategories, categorySearchDebounced]);
	useEffect(() => {
		if (
			informationId &&
			hasPermission([Permissions.PROJECT_LEADER, Permissions.PROJECT_MANAGER]) &&
			isSegFeatureEnabled(SegFlags.REVIEW_BY_CATEGORY)
		) {
			getAttachedCategoryIdsByInformation({ informationId }).then(setAttachedCategoryIds).catch(console.error);
		}
	}, [getAttachedCategoryIdsByInformation, informationId]);

	const handleUpdate = (newIds, currentIds, justDisabledIds) => {
		const payload = {};
		const addThematic = newIds.filter((nt) => !currentIds.some((ot) => ot === nt));
		const deleteThematic = currentIds.filter((nt) => !newIds.some((ot) => ot === nt));
		if (addThematic.length > 0) {
			payload.addThematic = addThematic;
		}
		if (deleteThematic.length > 0) {
			payload.deleteThematic = deleteThematic;
		}
		patchByInformation({ informationId }, payload)
			.then(() => {
				dispatch(
					updateNode({
						mode: selectedMode,
						pageConditions: { page: pageStart },
						informationId,
						updates: [{ property: "thematicCount", value: newIds.length }],
					})
				);
				setDisabledIds((prev) => prev.filter((cat) => !justDisabledIds.includes(cat)));
			})
			.catch((err) => {
				setCheckedIds(currentIds);
				loadCategories(categorySearchDebounced);
				setDisabledIds((prev) => prev.filter((cat) => !justDisabledIds.includes(cat)));
				console.error(err);
			});
	};
	const handleResetSearchText = () => {
		setCategorySearch("");
		setCategorySearchDebounced("");
	};
	const handleChangeSearchText = (event) => {
		const { value } = event.target;
		if (categorySearch.length === 0 && value.length > 0 && showSelected) {
			setShowSelected(false);
		}
		setCategorySearch(value);
		setIsLoading(true);
		debouncedFunction(() => {
			setIsLoading(false);
			setCategorySearchDebounced(value);
		});
	};
	const handleChangeShowSelected = (_, tab) => {
		setShowSelected((prev) => !prev);
		setSelectedTab(tab);
	};
	const getChildrenIdsList = (category) => {
		if (!category) {
			return [];
		}
		if (!Array.isArray(category.subThematic)) {
			return [category.id];
		}
		return [category.id, ...category.subThematic.map((cat) => getChildrenIdsList(cat))].flat();
	};
	const handleCheck = (checked, checkedCategory) => {
		let newCheckedIds = [];
		let justDisabledIds = [];
		if (checked) {
			justDisabledIds = getParentsIdsList(categories, checkedCategory);
			const result = checkedIds.concat(justDisabledIds);
			newCheckedIds = result.filter((cat, index) => result.indexOf(cat) === index);
		} else {
			justDisabledIds = getChildrenIdsList(checkedCategory);
			newCheckedIds = checkedIds.filter((prevId) => !justDisabledIds.some((childId) => childId === prevId));
			setAttachedCategoryIds((prev) => prev.filter((p) => !justDisabledIds.includes(p)));
		}
		setDisabledIds((prev) => prev.concat(justDisabledIds));
		handleUpdate(newCheckedIds, checkedIds, justDisabledIds);
		setCheckedIds(newCheckedIds);
	};
	const handleExpand = (expanded, category) => {
		if (!expandedIds.includes(category.id)) {
			dispatch(setExpandedIds([...expandedIds, category.id]));
		} else {
			dispatch(setExpandedIds(expandedIds.filter((item) => item !== category.id)));
		}
	};
	const handleOpenAddCategoryDialog = () => {
		setOpenCategoryDialog(true);
	};
	const handleCloseAddCategoryDialog = () => {
		setOpenCategoryDialog(false);
	};
	const handleSubmitAddCategoryDialog = (newCategory, __, newParent) => {
		const newParentId = newParent?.id || null;
		patchProjectTreeUpdate(
			{ projectId },
			{
				operation: ACTION_TYPES.add,
				name: newCategory.name,
				description: newCategory.description || null,
				parentId: newParentId,
				id: null,
			}
		).then((data) => {
			if (data.parentId) {
				getTreeChildrenForProject({ parentId: data.parentId, details: false })
					.then((oldList) => {
						setCategories((prev) =>
							updateTreeBranch(
								{
									tree: prev,
									idToReplace: data.parentId,
									newBranch: {
										...getCategoryFromTree(categories, data.parentId),
										subThematic: oldList,
										expanded: true,
									},
									iterativeBranchName: "subThematic",
								},
								{ sortKey: "name" }
							)
						);
					})
					.catch((err) => console.error(err));
			} else {
				loadCategories(categorySearchDebounced);
			}
			trackEvent("Category_new");
		});
		handleCloseAddCategoryDialog();
	};
	const handleAttachInformationCategory = (categoryId, attached) => {
		attachInformationCategory({ categoryId, informationId }, !attached)
			.then(() => {
				trackEvent("MyReview_Categories_Flag_content");
				if (attached) {
					setAttachedCategoryIds((prev) => prev.filter((p) => p !== categoryId));
				} else {
					setAttachedCategoryIds((prev) => [...prev, categoryId]);
				}
			})
			.catch(console.error);
	};
	return (
		<div className={styles.main}>
			<div className={styles.container}>
				{!disableEditing && (
					<>
						<div className={styles.container_filter}>
							<div>
								<ViewTabs
									className={styles.tabs}
									selectedTab={selectedTab}
									tabs={[
										{
											label: translate(
												"smart-review.cmp-details.option-panel.deliverables-tab.edit.deliverables-list.all"
											),
										},
										{
											label: translate(
												"smart-review.cmp-details.option-panel.deliverables-tab.edit.deliverables-list.selected",
												{
													checkedCount: checkedCounts || 0,
												}
											),
											disabled: Number(checkedCounts) === 0,
										},
									]}
									onChange={handleChangeShowSelected}
								/>
							</div>
							<CustomIconButton
								btnClassName={`${styles.rightIcon} ${
									disabledIds.length === 0 ? styles["rightIcon--translucent"] : ""
								}`}
								icon={icon.faSave}
								iconColor="var(--blue-500)"
								size="sm"
							/>
						</div>
						<div className={styles.container_search}>
							<SearchInput
								autoFocus
								className={styles.search}
								placeholder={translate(
									"smart-review.cmp-details.option-panel.categories-tab.edit.search"
								)}
								value={categorySearch}
								onBlur={() => dispatch(setDisableKeyEvents(false))}
								onChange={handleChangeSearchText}
								onClearSearch={handleResetSearchText}
								onFocus={() => dispatch(setDisableKeyEvents(true))}
							/>
							<CustomTooltip
								title={
									isProjectDirector
										? ""
										: translate(
												"smart-review.cmp-details.option-panel.categories-tab.missing-rights"
										  )
								}
							>
								<span>
									<CustomButton
										color="secondary"
										disabled={!isProjectDirector || isArchived}
										startIcon={icon.faPlus}
										variant="outlined"
										onClick={handleOpenAddCategoryDialog}
									>
										{translate("common:categories.button.new-thematic")}
									</CustomButton>
								</span>
							</CustomTooltip>
						</div>
					</>
				)}
				<div
					className={`${
						(!(
							hasPermission([Permissions.PROJECT_MANAGER, Permissions.PROJECT_LEADER]) &&
							hasCompanyPermission([Permissions.PROJECT_LEADER]) &&
							!disableEditing
						) &&
							styles["container_list--fullHeight"]) ||
						styles.container_list
					} ${(isLoading && styles.loadingContent) || ""}`}
				>
					{(isNotEmpty && (
						<CategoryList
							attachedCategoryIds={attachedCategoryIds}
							categoryList={categories}
							checkedIds={checkedIds}
							disabledIds={disabledIds}
							disableEditing={disableEditing}
							editable={
								hasPermission([Permissions.PROJECT_MANAGER, Permissions.PROJECT_LEADER]) &&
								hasCompanyPermission([Permissions.PROJECT_LEADER]) &&
								!disableEditing &&
								!isArchived
							}
							expandedIds={expandedIds}
							forceExpanded={selectedTab === 1 || (disableEditing && selectedTab === 0)}
							informationId={informationId}
							showSelected={showSelected || disableEditing}
							onAttachInformationCategory={handleAttachInformationCategory}
							onCheck={handleCheck}
							onExpand={handleExpand}
						/>
					)) || (
						<div className={styles.emptyState}>
							<IconComponent
								className={styles.emptyStateIcon}
								color="var(--blue-500)"
								icon={icon.faInfoCircle}
							/>
							{translate("smart-review.cmp-details.option-panel.categories-tab.edit.empty-state")}
						</div>
					)}
				</div>
			</div>
			<CategoryDialog
				isNew
				availableParents={!categorySearchDebounced && categories}
				open={openAddCategoryDialog}
				onClose={handleCloseAddCategoryDialog}
				onSubmit={handleSubmitAddCategoryDialog}
			/>
		</div>
	);
}
